import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  HomeOutlinedIcon,
  AddIcon,
  ArrowBackIcon,
  DeleteIcon,
} from "../../../icons";
import GlobalLoader from "../../common/GlobalLoader";
import ImageUploadPopup from "./ImageUploadPopup";
import ImagePreviewPopup from "../../common/ImagePreviewPopup";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import {
  getImageFolderDetails,
  deleteImage,
} from "../../../helper/apicalls/imageUpload";
import "../../../styles/admin/imageUpload.scss";

const ImageFolderDetailsPage = () => {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [folderDetails, setFolderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [deletingImage, setDeletingImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({
    isOpen: false,
    imageId: null,
    imageName: "",
  });

  const fetchFolderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getImageFolderDetails(folderId);
      const data = response?.data || response;
      setFolderDetails(data);
    } catch (err) {
      console.error("Error fetching folder details:", err);
      setError("Failed to fetch folder details");
      setFolderDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (folderId) {
      fetchFolderDetails();
    }
  }, [folderId]);

  const handleUploadImage = () => {
    setShowUploadPopup(true);
  };

  const handleDeleteImage = async (imageId, imageName) => {
    setConfirmDelete({
      isOpen: true,
      imageId,
      imageName: imageName || `Image ${imageId}`,
    });
  };

  const confirmDeleteImage = async () => {
    const { imageId } = confirmDelete;
    try {
      setDeletingImage(imageId);
      await deleteImage(folderId, imageId);
      await fetchFolderDetails();
      setConfirmDelete({ isOpen: false, imageId: null, imageName: "" });
    } catch (err) {
      console.error("Error deleting image:", err);
      alert("Failed to delete image");
    } finally {
      setDeletingImage(null);
    }
  };

  const handleUploadSuccess = () => {
    setShowUploadPopup(false);
    fetchFolderDetails();
  };

  const handleBackToFolders = () => {
    navigate("/admin/image-folders");
  };

  const handleImageClick = (image) => {
    setPreviewImage({
      url: getImageUrl(image.image_url),
      name: image.name || `Image ${image.id}`,
      id: image.id,
      fileName: image.file_name || image.name || `Image ${image.id}`,
    });
  };

  const closePreview = () => {
    setPreviewImage(null);
  };

  const getImageUrl = (imagePath) => {
    if (imagePath.startsWith("http")) {
      return imagePath;
    }
    return imagePath;
  };

  return (
    <>
      <GlobalLoader visible={loading} text="Loading folder details..." />
      <div className="image-upload-container">
        <div className="image-upload-card">
          <div className="image-upload-header">
            <div className="header-left">
              <button
                className="icon-btn back-btn"
                onClick={handleBackToFolders}
                title="Back to folders"
              >
                <ArrowBackIcon />
              </button>
              <h1 className="page-title">
                {folderDetails?.name || folderDetails?.folder_name || "Folder"}{" "}
                - Images
              </h1>
            </div>
            <div className="header-actions">
              <button className="primary-btn" onClick={handleUploadImage}>
                <AddIcon />
                Upload Image
              </button>
            </div>
          </div>
          <div className="image-grid-wrapper custom-scrollbar">
            <div className="image-grid">
              {!loading &&
                !error &&
                folderDetails?.images_data?.map((image) => (
                  <div
                    key={image.id}
                    className="image-item"
                    onClick={() => handleImageClick(image)}
                  >
                    <div className="image-container">
                      <img
                        src={getImageUrl(image.image_url)}
                        alt={image.name || "Image"}
                        onError={(e) => {
                          e.target.src = "/placeholder-image.png";
                        }}
                      />
                    </div>
                    <div className="image-info">
                      <div className="image-name">
                        {image.name || `Image ${image.id}`}
                      </div>
                    </div>
                    <button
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(image.id, image.name);
                      }}
                      title="Delete image"
                      disabled={deletingImage === image.id}
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                ))}

              {error && (
                <div className="error-state">
                  <div className="error-message">{error}</div>
                </div>
              )}

              {!loading &&
                !error &&
                (!folderDetails?.images_data ||
                  folderDetails.images_data.length === 0) && (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <AddIcon />
                    </div>
                    <h3 className="empty-title">No images in this folder</h3>
                    <p className="empty-description">
                      Upload your first image to get started
                    </p>
                    <button
                      className="empty-action-btn"
                      onClick={handleUploadImage}
                    >
                      <AddIcon />
                      Upload First Image
                    </button>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      {showUploadPopup && (
        <ImageUploadPopup
          open={showUploadPopup}
          handleClose={() => setShowUploadPopup(false)}
          folderId={folderId}
          onUploadSuccess={handleUploadSuccess}
        />
      )}

      {previewImage && (
        <ImagePreviewPopup
          isOpen={true}
          onClose={closePreview}
          imageUrl={previewImage.url}
          imageName={previewImage.name}
          imageId={previewImage.id}
          imageFileName={previewImage.fileName}
        />
      )}

      {confirmDelete.isOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={confirmDelete.isOpen}
          setIsConfirmDeleteOpen={(isOpen) =>
            setConfirmDelete({ isOpen, imageId: null, imageName: "" })
          }
          handleDelete={confirmDeleteImage}
          title="Delete Image"
          message={`Are you sure you want to delete "${confirmDelete.imageName}"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      )}
    </>
  );
};

export default ImageFolderDetailsPage;
